import React from 'react';
import databaseAPI from '../../api/databaseAPI.js';
import AddOrderProductSearch from './AddOrderProductSearch';

/**
 * Create Order modal (manual + CSV bulk create).
 */
export default function AddOrderModal({
  show,
  onClose,
  addOrderMode,
  setAddOrderMode,
  newOrder,
  setNewOrder,
  billingSameAsShipping,
  setBillingSameAsShipping,
  orderCsvData,
  setOrderCsvFile,
  setOrderCsvData,
  orderCsvProcessing,
  handleOrderCsvFileChange,
  downloadOrderCreateSampleCsv,
  processOrderCreateCsv,
  fetchOrders,
}) {
  if (!show) return null;

  return (
    <div className="modal-overlay">
      <div className="dispute-modal add-order-modal">
        <div className="modal-header">
          <div>
            <h3>Create Order</h3>
            <p className="add-order-subtitle">Fill customer details, add products, and create order in one flow.</p>
          </div>
          <button onClick={onClose} className="close-btn">Close</button>
        </div>
        <div className="modal-content">
          {/* Mode Switch */}
          <div className="form-grid add-order-mode-wrap" style={{ marginBottom: 12 }}>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <div className="add-order-mode-toggle">
                <button type="button" className={`add-order-mode-btn ${addOrderMode === 'manual' ? 'active' : ''}`} onClick={() => setAddOrderMode('manual')}>Manual</button>
                <button type="button" className={`add-order-mode-btn ${addOrderMode === 'csv' ? 'active' : ''}`} onClick={() => setAddOrderMode('csv')}>CSV</button>
              </div>
            </div>
          </div>

          {addOrderMode === 'csv' ? (
            <div className="form-section">
              <h4>Bulk Create via CSV</h4>
              <p>Upload CSV with these columns: customer_email, first_name (optional), last_name (optional), currency (optional), variant_id, quantity, address1, city, state, country, zip, phone, coupon (optional). We will fetch product name and price from Shopify using variant_id. Extra columns will be ignored.</p>
              <div className="form-grid">
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <button type="button" className="download-sample-btn" onClick={downloadOrderCreateSampleCsv}>Download Sample CSV</button>
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <input type="file" accept=".csv" onChange={handleOrderCsvFileChange} />
                </div>
                {orderCsvData.length > 0 && (
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <strong>Preview ({orderCsvData.length} rows)</strong>
                    <ul style={{ maxHeight: 160, overflow: 'auto', paddingLeft: 16 }}>
                      {orderCsvData.slice(0, 5).map((r, i) => (
                        <li key={i}>
                          {r.customer_email} • {r.variant_id} x{r.quantity}
                          {r.product_title ? ` • ${r.product_title}` : ''}
                          {r.price ? ` • $${r.price}` : ''}
                          {r.coupon_code ? ` • coupon: ${r.coupon_code}` : ''}
                          {` • ${r.shipping_address.address1}, ${r.shipping_address.city}`}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="form-group" style={{ gridColumn: '1 / -1', display: 'flex', gap: 8 }}>
                  <button type="button" className="process-csv-btn" disabled={orderCsvProcessing || orderCsvData.length === 0} onClick={processOrderCreateCsv}>
                    {orderCsvProcessing ? 'Processing...' : 'Create Orders'}
                  </button>
                  <button type="button" className="reset-csv-btn" onClick={() => { setOrderCsvFile(null); setOrderCsvData([]); }}>Reset</button>
                  <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
                </div>
              </div>
            </div>
          ) : (
            <div className="form-grid add-order-top-grid">
              <div className="form-group">
                <label>Customer Email</label>
                <input type="email" value={newOrder.customer_email} onChange={e => setNewOrder({ ...newOrder, customer_email: e.target.value })} />
              </div>
              <div className="form-group">
                <label>First Name</label>
                <input type="text" value={newOrder.customer_first_name} onChange={e => setNewOrder({ ...newOrder, customer_first_name: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input type="text" value={newOrder.customer_last_name} onChange={e => setNewOrder({ ...newOrder, customer_last_name: e.target.value })} />
              </div>
              {/* Total price removed; will be auto-calculated from selected products */}
              <div className="form-group">
                <label>Currency</label>
                <select
                  value={newOrder.currency}
                  onChange={e => setNewOrder({ ...newOrder, currency: e.target.value })}
                  className="status-filter"
                  style={{ minWidth: 'unset' }}
                >
                  <option value="INR">INR - Indian Rupee</option>
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - British Pound</option>
                  <option value="AUD">AUD - Australian Dollar</option>
                  <option value="CAD">CAD - Canadian Dollar</option>
                  <option value="JPY">JPY - Japanese Yen</option>
                </select>
              </div>
            </div>
          )}
          {addOrderMode === 'manual' && (
            <>
              <div className="form-section">
                <h4>Add Products</h4>
                <div className="form-grid">
                  <AddOrderProductSearch
                    onAddVariant={(p, v) => {
                      const existingIndex = newOrder.line_items.findIndex(li => li.variant_id === v.id);
                      let nextItems = [...newOrder.line_items];
                      if (existingIndex >= 0) {
                        nextItems[existingIndex] = {
                          ...nextItems[existingIndex],
                          quantity: (nextItems[existingIndex].quantity || 1) + 1,
                          price: v.price,
                          title: p.title
                        };
                      } else {
                        nextItems.push({ variant_id: v.id, quantity: 1, requires_shipping: v.requires_shipping !== false, price: v.price, title: p.title, product_id: p.id });
                      }
                      const total = nextItems.reduce((sum, it) => sum + (parseFloat(it.price || 0) * (it.quantity || 1)), 0);
                      setNewOrder({ ...newOrder, line_items: nextItems, total_price: total.toFixed(2) });
                    }}
                  />
                  {newOrder.line_items.length > 0 && (
                    <div className="add-order-selected-items">
                      <strong>Selected Items</strong>
                      <ul className="add-order-selected-list" style={{ margin: '8px 0 0 16px' }}>
                        {newOrder.line_items.map((li, idx) => (
                          <li key={idx}>
                            {(li.title || 'Variant')} — ${li.price || '-'}
                            <span style={{ marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <button
                                type="button"
                                className="apply-bulk-btn"
                                onClick={() => {
                                  const next = [...newOrder.line_items];
                                  const currentQty = Math.max(1, parseInt(next[idx].quantity || 1, 10));
                                  next[idx] = { ...next[idx], quantity: Math.max(1, currentQty - 1) };
                                  const total = next.reduce((sum, it) => sum + (parseFloat(it.price || 0) * (parseInt(it.quantity || 1, 10))), 0);
                                  setNewOrder({ ...newOrder, line_items: next, total_price: total.toFixed(2) });
                                }}
                                aria-label="Decrease quantity"
                              >
                                −
                              </button>
                              <input
                                type="number"
                                min={1}
                                value={li.quantity || 1}
                                onChange={(e) => {
                                  const val = Math.max(1, parseInt(e.target.value || '1', 10));
                                  const next = [...newOrder.line_items];
                                  next[idx] = { ...next[idx], quantity: val };
                                  const total = next.reduce((sum, it) => sum + (parseFloat(it.price || 0) * (parseInt(it.quantity || 1, 10))), 0);
                                  setNewOrder({ ...newOrder, line_items: next, total_price: total.toFixed(2) });
                                }}
                                style={{ width: 60, textAlign: 'center' }}
                              />
                              <button
                                type="button"
                                className="apply-bulk-btn"
                                onClick={() => {
                                  const next = [...newOrder.line_items];
                                  const currentQty = Math.max(1, parseInt(next[idx].quantity || 1, 10));
                                  next[idx] = { ...next[idx], quantity: currentQty + 1 };
                                  const total = next.reduce((sum, it) => sum + (parseFloat(it.price || 0) * (parseInt(it.quantity || 1, 10))), 0);
                                  setNewOrder({ ...newOrder, line_items: next, total_price: total.toFixed(2) });
                                }}
                                aria-label="Increase quantity"
                              >
                                +
                              </button>
                            </span>
                            <button
                              type="button"
                              className="reset-csv-btn"
                              style={{ marginLeft: 8, padding: '4px 8px' }}
                              onClick={() => {
                                const next = newOrder.line_items.filter((_, i) => i !== idx);
                                const total = next.reduce((sum, it) => sum + (parseFloat(it.price || 0) * (it.quantity || 1)), 0);
                                setNewOrder({ ...newOrder, line_items: next, total_price: total.toFixed(2) });
                              }}
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                      <div className="add-order-total" style={{ marginTop: 6 }}>
                        <strong>Total:</strong> ${newOrder.total_price || '0.00'} {newOrder.currency}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="form-section">
                <h4>Discount</h4>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Coupon Code (optional)</label>
                    <input
                      type="text"
                      value={newOrder.coupon_code || ''}
                      onChange={(e) => setNewOrder({ ...newOrder, coupon_code: e.target.value })}
                      placeholder="e.g. SAVE10"
                    />
                  </div>
                </div>
              </div>
              <div className="form-section">
                <h4>Shipping Address</h4>
                <div className="form-grid">
                  {false && (<div className="form-group"><label>First Name</label><input type="text" value={newOrder.shipping_address.first_name || ''} onChange={() => {}} /></div>)}
                  {false && (<div className="form-group"><label>Last Name</label><input type="text" value={newOrder.shipping_address.last_name || ''} onChange={() => {}} /></div>)}
                  <div className="form-group"><label>Address 1</label><input type="text" value={newOrder.shipping_address.address1} onChange={e => {
                    const shipping = { ...newOrder.shipping_address, address1: e.target.value };
                    const billing = billingSameAsShipping ? { ...shipping } : newOrder.billing_address;
                    setNewOrder({ ...newOrder, shipping_address: shipping, billing_address: billing });
                  }} /></div>
                  <div className="form-group"><label>City</label><input type="text" value={newOrder.shipping_address.city} onChange={e => {
                    const shipping = { ...newOrder.shipping_address, city: e.target.value };
                    const billing = billingSameAsShipping ? { ...shipping } : newOrder.billing_address;
                    setNewOrder({ ...newOrder, shipping_address: shipping, billing_address: billing });
                  }} /></div>
                  <div className="form-group"><label>State</label><input type="text" value={newOrder.shipping_address.province} onChange={e => {
                    const shipping = { ...newOrder.shipping_address, province: e.target.value };
                    const billing = billingSameAsShipping ? { ...shipping } : newOrder.billing_address;
                    setNewOrder({ ...newOrder, shipping_address: shipping, billing_address: billing });
                  }} /></div>
                  <div className="form-group"><label>Country</label><input type="text" value={newOrder.shipping_address.country} onChange={e => {
                    const shipping = { ...newOrder.shipping_address, country: e.target.value };
                    const billing = billingSameAsShipping ? { ...shipping } : newOrder.billing_address;
                    setNewOrder({ ...newOrder, shipping_address: shipping, billing_address: billing });
                  }} /></div>
                  <div className="form-group"><label>Zip</label><input type="text" value={newOrder.shipping_address.zip} onChange={e => {
                    const shipping = { ...newOrder.shipping_address, zip: e.target.value };
                    const billing = billingSameAsShipping ? { ...shipping } : newOrder.billing_address;
                    setNewOrder({ ...newOrder, shipping_address: shipping, billing_address: billing });
                  }} /></div>
                  <div className="form-group"><label>Phone</label><input type="text" value={newOrder.shipping_address.phone_number} onChange={e => {
                    const shipping = { ...newOrder.shipping_address, phone_number: e.target.value };
                    const billing = billingSameAsShipping ? { ...shipping } : newOrder.billing_address;
                    setNewOrder({ ...newOrder, shipping_address: shipping, billing_address: billing });
                  }} /></div>
                </div>
              </div>

              <div className="form-section">
                <div className="form-group add-order-billing-toggle">
                  <div className="add-order-billing-toggle-title">Billing Preference</div>
                  <label htmlFor="sameBilling" className="add-order-billing-checkbox">
                    <input id="sameBilling" type="checkbox" checked={billingSameAsShipping} onChange={(e) => {
                      const checked = e.target.checked;
                      setBillingSameAsShipping(checked);
                      setNewOrder(prev => ({
                        ...prev,
                        billing_address: checked ? { ...prev.shipping_address } : prev.billing_address
                      }));
                    }} />
                    <span>Billing address same as Shipping</span>
                  </label>
                </div>
                <h4>Billing Address</h4>
                <div className="form-grid">
                  {false && (<div className="form-group"><label>First Name</label><input type="text" value={newOrder.billing_address.first_name || ''} disabled={billingSameAsShipping} onChange={() => {}} /></div>)}
                  {false && (<div className="form-group"><label>Last Name</label><input type="text" value={newOrder.billing_address.last_name || ''} disabled={billingSameAsShipping} onChange={() => {}} /></div>)}
                  <div className="form-group"><label>Address 1</label><input type="text" value={newOrder.billing_address.address1} disabled={billingSameAsShipping} onChange={e => setNewOrder({ ...newOrder, billing_address: { ...newOrder.billing_address, address1: e.target.value } })} /></div>
                  <div className="form-group"><label>City</label><input type="text" value={newOrder.billing_address.city} disabled={billingSameAsShipping} onChange={e => setNewOrder({ ...newOrder, billing_address: { ...newOrder.billing_address, city: e.target.value } })} /></div>
                  <div className="form-group"><label>State</label><input type="text" value={newOrder.billing_address.province} disabled={billingSameAsShipping} onChange={e => setNewOrder({ ...newOrder, billing_address: { ...newOrder.billing_address, province: e.target.value } })} /></div>
                  <div className="form-group"><label>Country</label><input type="text" value={newOrder.billing_address.country} disabled={billingSameAsShipping} onChange={e => setNewOrder({ ...newOrder, billing_address: { ...newOrder.billing_address, country: e.target.value } })} /></div>
                  <div className="form-group"><label>Zip</label><input type="text" value={newOrder.billing_address.zip} disabled={billingSameAsShipping} onChange={e => setNewOrder({ ...newOrder, billing_address: { ...newOrder.billing_address, zip: e.target.value } })} /></div>
                  <div className="form-group"><label>Phone</label><input type="text" value={newOrder.billing_address.phone_number} disabled={billingSameAsShipping} onChange={e => setNewOrder({ ...newOrder, billing_address: { ...newOrder.billing_address, phone_number: e.target.value } })} /></div>
                </div>
              </div>
              <div className="modal-actions">
                <button
                  onClick={async () => {
                    if (!newOrder.customer_email || !newOrder.total_price || newOrder.line_items.length === 0) {
                      alert('Please fill customer email, total price, and at least one line item.');
                      return;
                    }
                    const payload = {
                      ...newOrder,
                      ...(newOrder.coupon_code ? { discount_codes: [{ code: newOrder.coupon_code }] } : {})
                    };
                    const res = await databaseAPI.createOrder(payload);
                    if (!res.success) {
                      alert('Failed to create order: ' + (res.error || res.message));
                      return;
                    }
                    alert(`Order created: ${res.order_number} (ID: ${res.order_id})`);
                    onClose();
                    setNewOrder({
                      customer_email: '', customer_first_name: '', customer_last_name: '', total_price: '', currency: 'USD',
                      coupon_code: '',
                      line_items: [],
                      shipping_address: { address1: '', city: '', province: '', country: 'India', zip: '', phone_number: '' },
                      billing_address: { address1: '', city: '', province: '', country: 'India', zip: '', phone_number: '' }
                    });
                    await fetchOrders();
                  }}
                  className="submit-dispute-btn"
                >
                  Create Order
                </button>
                <button onClick={onClose} className="cancel-btn">Cancel</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
